// The preferences that belong to the person rather than the workspace, and the
// one screen-wide answer every clock in the app reads: which zone am I in.
//
// It is a context rather than a per-screen fetch because the answer has to be
// the same everywhere at once. Compose, the calendar grid, the posts list and
// the dashboard all render times, and a screen that loaded the preference a
// moment later than its neighbour would show two different clocks on one page.

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from "react";
import { supabase } from "./supabase";
import { deviceZone, isKnownZone } from "./timezone";
import { logError } from "./logger";

export interface UserSettings {
  /** null = follow whatever device this is. See the migration for why. */
  timezone: string | null;
  /** Breadcrumb for the picker only; nothing else should read it. */
  country_code: string | null;
}

interface SettingsState extends UserSettings {
  /**
   * The zone to actually render and schedule in: the saved one, or this
   * device's when there is none. Never null, so no caller has to decide what
   * to do about a missing preference.
   */
  zone: string;
  /** True when `zone` is the device's because nothing was saved. */
  automatic: boolean;
  /** null until the first load lands — screens can tell "none" from "not yet". */
  ready: boolean;
  save: (next: UserSettings) => Promise<void>;
}

const Ctx = createContext<SettingsState | null>(null);

export async function loadUserSettings(): Promise<UserSettings> {
  const { data: auth } = await supabase.auth.getUser();
  const id = auth.user?.id;
  if (!id) return { timezone: null, country_code: null };

  const { data, error } = await supabase
    .from("user_settings")
    .select("timezone, country_code")
    .eq("user_id", id)
    .maybeSingle();
  if (error) throw error;
  return { timezone: data?.timezone ?? null, country_code: data?.country_code ?? null };
}

export async function saveUserSettings(next: UserSettings): Promise<void> {
  const { data: auth } = await supabase.auth.getUser();
  const id = auth.user?.id;
  if (!id) throw new Error("Not signed in.");

  const { error } = await supabase
    .from("user_settings")
    .upsert({ user_id: id, ...next }, { onConflict: "user_id" });
  if (error) throw error;
}

export function UserSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<UserSettings>({ timezone: null, country_code: null });
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let live = true;
    loadUserSettings()
      .then((s) => { if (live) setSettings(s); })
      .catch((e) => {
        // A preference that will not load must not take a screen down with it.
        // Falling back to the device zone is exactly the pre-migration
        // behaviour, which is the right thing to degrade to.
        void logError("app", e, { step: "load_user_settings" });
      })
      .finally(() => { if (live) setReady(true); });
    return () => { live = false; };
  }, []);

  const save = useCallback(async (next: UserSettings) => {
    await saveUserSettings(next);
    setSettings(next);
  }, []);

  const value = useMemo<SettingsState>(() => {
    // A saved zone this browser cannot resolve — an old tzdb, a renamed zone —
    // is worse than no zone: every time on screen would be silently wrong. Fall
    // back to the device and let the profile card say the saved one is stale.
    const saved = settings.timezone && isKnownZone(settings.timezone) ? settings.timezone : null;
    return {
      ...settings,
      zone: saved ?? deviceZone(),
      automatic: saved === null,
      ready,
      save,
    };
  }, [settings, ready, save]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useUserSettings(): SettingsState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useUserSettings outside UserSettingsProvider");
  return ctx;
}

/**
 * The zone to schedule and render in. The shorthand almost every caller wants,
 * so a screen that only needs the clock does not have to know a settings
 * context exists.
 */
export function useZone(): string {
  return useUserSettings().zone;
}
